import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import ThemePanel from './ThemePanel';
import WorkshopChat from './WorkshopChat';
import { useZoom } from '../Common/ZoomSlider';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { WorkshopMobileToggle } from './WorkshopMobileToggle';
import './ThemeWorkshopLayout.css';

/**
 * ThemeWorkshopLayout - Layout for Theme Workshop sessions
 *
 * Unlike structural workshops (WorkshopLayout), Theme Workshop is purely
 * conversational with no card-based suggestions. It combines:
 * - ThemePanel at top (editable theme field + Generate/Save)
 * - WorkshopChat below (conversational AI)
 *
 * Props:
 * - sessionId: UUID of the workshop session
 * - storyId: UUID of the story
 * - onBack: () => void - called to exit workshop
 */
export default function ThemeWorkshopLayout({
  sessionId,
  storyId,
  onBack
}) {
  const [chatZoom, setChatZoom] = useZoom('theme-workshop-chat-zoom');

  // Mobile toggle state
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const [mobileView, setMobileView] = useState('chat');

  const handleThemeSaved = (theme) => {
    console.log('[ThemeWorkshop] Theme saved:', theme);
    // Could trigger context refresh or other actions here
  };

  return (
    <div className="theme-workshop-layout">
      {/* Header */}
      <div className="theme-workshop-layout__header">
        {onBack && (
          <button
            className="theme-workshop-layout__back-btn"
            onClick={onBack}
            title="Exit workshop"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <h2 className="theme-workshop-layout__title">Theme Workshop</h2>
      </div>

      {isMobile && (
        <WorkshopMobileToggle
          views={[
            { id: 'chat', label: 'Chat' },
            { id: 'content', label: 'Content' },
          ]}
          activeView={mobileView}
          onViewChange={setMobileView}
        />
      )}

      {/* Theme Panel */}
      {(!isMobile || mobileView === 'content') && (
        <div className="theme-workshop-layout__panel">
          <ThemePanel
            storyId={storyId}
            sessionId={sessionId}
            onThemeSaved={handleThemeSaved}
          />
        </div>
      )}

      {/* Chat Area */}
      {(!isMobile || mobileView === 'chat') && (
        <div className="theme-workshop-layout__chat">
          <WorkshopChat
            sessionId={sessionId}
            storyId={storyId}
            zoom={chatZoom}
            onZoomChange={setChatZoom}
          />
        </div>
      )}
    </div>
  );
}

export { ThemeWorkshopLayout };
